import { authMiddleware } from '../middleware/authMiddleware';
import { User } from '@supabase/supabase-js';

/**
 * Service for handling authentication operations
 * This service uses the authMiddleware to perform operations
 * and provides additional business logic and error handling
 */

/**
 * Check if a user is authenticated
 * @returns Whether the user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    console.log('Checking if user is authenticated');
    const session = await authMiddleware.getSession();
    const isAuth = !!session;
    console.log('Authentication check result:', isAuth);
    return isAuth;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
}

/**
 * Get the current authenticated user
 * @returns The current user or null if not authenticated
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    console.log('Getting current user');
    return await authMiddleware.getCurrentUser();
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Get the user's salon ID
 * @returns The salon ID or null if not found
 */
export async function getUserSalonId(): Promise<string | null> {
  try {
    console.log('Getting user salon ID');
    const user = await authMiddleware.getCurrentUser();
    
    if (!user) {
      console.log('No authenticated user found');
      return null;
    }
    
    // Get the salon_user record
    const { data, error } = await authMiddleware.getClient()
      .from('salon_users')
      .select('salon_id')
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (error) {
      console.error('Error getting salon ID:', error);
      return null;
    }
    
    if (!data) {
      console.log('No salon association found');

      if (!user.email) {
        console.log('Authenticated user has no email address');
        return null;
      }
      
      // Try to find salon by email
      const { data: salonData, error: salonError } = await authMiddleware.getClient()
        .from('salons')
        .select('id')
        .eq('email', user.email)
        .maybeSingle();
      
      if (salonError || !salonData) {
        console.log('No salon found by email');
        return null;
      }
      
      console.log('Found salon by email:', salonData.id);
      return salonData.id;
    }
    
    console.log('Found salon ID:', data.salon_id);
    return data.salon_id;
  } catch (error) {
    console.error('Error getting user salon ID:', error);
    return null;
  }
}

/**
 * Sign in with email and password
 * @param email User email
 * @param password User password
 * @returns Success status and any error message
 */
export async function signIn(email: string, password: string) {
  console.log('Starting sign in process with authService');
  
  // Validate input
  if (!email || !password) {
    console.error('Missing email or password');
    return { 
      success: false, 
      error: 'Email and password are required' 
    };
  }
  
  // Attempt sign in with rate limit protection
  try {
    console.log('Calling authMiddleware.signIn');
    const result = await authMiddleware.signIn({ email, password });
    
    if (!result.success) {
      console.error('Sign in failed:', result.error);
      return { 
        success: false, 
        error: result.error?.message || 'Failed to sign in' 
      };
    }
    
    console.log('Sign in successful, checking salon association');
    
    // Check if the user has a salon association
    const salonId = await getUserSalonId();
    
    if (!salonId) {
      console.log('No salon association found, redirecting to registration');
      return { 
        success: true, 
        requiresRegistration: true 
      };
    }
    
    console.log('Salon association found, sign in complete');
    return { 
      success: true, 
      salonId 
    };
  } catch (error) {
    console.error('Error during sign in:', error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred during sign in';
    return { 
      success: false, 
      error: errorMessage 
    };
  }
}

// NOTE: Registration is handled via `useAuthMiddleware` -> `authMiddleware.signUp`.
// The previous `authService.registerSalon` wrapper was unused and has been removed.

/**
 * Request a password reset email
 * @param email User email
 * @returns Success status
 */
export async function requestPasswordReset(email: string) {
  console.log('Requesting password reset for:', email);
  
  if (!email) {
    console.error('Missing email for password reset');
    return { 
      success: false, 
      error: 'Email is required' 
    };
  }
  
  try {
    const result = await authMiddleware.resetPassword({ email });
    
    if (!result.success) {
      console.error('Password reset request failed:', result.error);
      return { 
        success: false, 
        error: result.error?.message || 'Failed to send password reset email' 
      };
    }
    
    console.log('Password reset email sent successfully');
    return { success: true };
  } catch (error) {
    console.error('Error requesting password reset:', error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred during password reset';
    return { 
      success: false, 
      error: errorMessage 
    };
  }
}

/**
 * Verify a password reset token
 * @param token The reset token
 * @param email User email
 * @returns Success status and user data if valid
 */
export async function verifyPasswordResetToken(token: string, email: string) {
  console.log('Verifying password reset token');
  
  if (!token || !email) {
    console.error('Missing token or email for verification');
    return { 
      valid: false, 
      error: 'Token and email are required' 
    };
  }
  
  try {
    const result = await authMiddleware.verifyPasswordToken({ token, email });
    
    if (!result.success) {
      console.error('Token verification failed:', result.error);
      return { 
        valid: false, 
        error: result.error?.message || 'Invalid or expired token' 
      };
    }
    
    console.log('Token verified successfully');
    return { 
      valid: true, 
      user: result.data?.user 
    };
  } catch (error) {
    console.error('Error verifying token:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to verify token';
    return { 
      valid: false, 
      error: errorMessage 
    };
  }
}

/**
 * Set a new password after reset
 * @param token Reset token
 * @param email User email
 * @param password New password
 * @returns Success status
 */
export async function setNewPassword(token: string, email: string, password: string) {
  console.log('Setting new password');
  
  if (!token || !email || !password) {
    console.error('Missing required parameters for password reset');
    return { 
      success: false, 
      error: 'Token, email, and password are required' 
    };
  }
  
  try {
    const result = await authMiddleware.setPassword({ token, email, password });
    
    if (!result.success) {
      console.error('Set password failed:', result.error);
      return { 
        success: false, 
        error: result.error?.message || 'Failed to set password' 
      };
    }
    
    console.log('Password set successfully');
    return { success: true };
  } catch (error) {
    console.error('Error setting password:', error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred while setting password';
    return { 
      success: false, 
      error: errorMessage 
    };
  }
}

/**
 * Sign out the current user
 * @returns Success status
 */
export async function signOut() {
  console.log('Signing out user');
  
  try {
    // First, clear all storage
    console.log('Clearing local storage');
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear cookies
    console.log('Clearing cookies');
    document.cookie.split(';').forEach(cookie => {
      document.cookie = cookie
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
    
    // Sign out from Supabase
    console.log('Signing out from Supabase');
    const result = await authMiddleware.signOut();
    
    if (!result.success) {
      console.error('Sign out failed:', result.error);
      // Continue with redirect even if there's an error
    }
    
    console.log('Sign out complete, redirecting to login');
    
    // Use window.location for a full page reload
    window.location.href = '/login';
    
    return { success: true };
  } catch (error) {
    console.error('Error during sign out:', error);
    
    // Even if there's an error, clear local state and redirect
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
    
    const errorMessage = error instanceof Error ? error.message : 'An error occurred during logout';
    return { 
      success: false, 
      error: errorMessage 
    };
  }
}